import React from "react";
import { ClassNameProps, Column, Modal, Row, StyleProps } from "@hex-insights/core";
import { TextLineLoadingPlaceholder } from "../../../../Components/LoadingPlaceholder";
import { Newsletter, User, UserNameAndImageFragment } from "../../../../Utilities";
import * as ModelComponents from "../../_General";
import { SimpleSharingView, SimpleSharingViewProps } from "../../SharingRecord";
import * as SocialCommentComponents from "../../SocialComment";
import * as SocialReactionComponents from "../../SocialReaction";
import { NewsletterCard, NewsletterCardLoadingPlaceholder } from "../Card";
import { AuthorTimestampView } from "../General";
import { NewsletterModal } from "../Modal";

export type NewsletterInTimelineItem = Pick<
	Newsletter,
	"id" | "title" | "imageURL" | "imageKey" | "imageContentType" | "body" | "releaseAt"
> & {
	author: (Pick<User, "id"> & UserNameAndImageFragment) | null;
	socialReactions: SocialReactionComponents.ReactionsViewProps["reactions"];
	socialComments: SocialCommentComponents.CommentsViewProps["comments"];
	sharingRecord: SimpleSharingViewProps["sharingRecord"] | null;
};

export type TimelineItemProps = {
	newsletter: NewsletterInTimelineItem | null;
	isLoading?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function TimelineItem({ newsletter, isLoading = false, className, style }: TimelineItemProps) {
	const viewerReaction = newsletter?.socialReactions.find((reaction) => reaction.author?.isCurrent) ?? null;
	const newsletterID = newsletter?.id;
	const reactionFormValues = React.useMemo(() => ({ newsletterID: newsletterID ?? null }), [newsletterID]);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	if (!newsletter && isLoading) {
		return (
			<div className={className} style={style}>
				<Column justify="spaced-start">
					<ModelComponents.AuthorTimestampViewLoadingPlaceholder withTimestamp />
					<NewsletterCardLoadingPlaceholder style={{ maxWidth: "22rem" }} />
					<Row justify="space-between">
						<TextLineLoadingPlaceholder>Be the first to like this</TextLineLoadingPlaceholder>
						<TextLineLoadingPlaceholder style={{ marginLeft: "0.5rem" }}>View</TextLineLoadingPlaceholder>
					</Row>
				</Column>
			</div>
		);
	}
	if (!newsletter) {
		return null;
	}

	return (
		<div className={className} style={style}>
			<Column justify="spaced-start" verticalSpacing="1rem">
				<Row justify="space-between">
					<AuthorTimestampView
						newsletter={newsletter}
						timestampVariant="relative"
						style={{ width: "100%", overflow: "hidden", marginRight: "1rem" }}
					/>
					<SocialReactionComponents.SocialReactionToggleButton
						instanceID={viewerReaction?.id}
						formValues={reactionFormValues}
						iconSize={24}
					/>
				</Row>

				{!!newsletter.sharingRecord && <SimpleSharingView sharingRecord={newsletter.sharingRecord} size="small" />}

				<NewsletterCard newsletter={newsletter} style={{ maxWidth: "22rem" }} />

				<Row justify="space-between">
					<SocialReactionComponents.ReactionsView newsletterID={newsletter.id} reactions={newsletter.socialReactions} />

					<SocialCommentComponents.CommentsViewButton
						numComments={newsletter.socialComments.length}
						onClick={openModal}
					/>
				</Row>
			</Column>

			<Modal.If condition={isModalOpen}>
				<NewsletterModal newsletter={newsletter} onClose={closeModal} />
			</Modal.If>
		</div>
	);
}
